import React, { useRef, useEffect } from "react";
import { Icon, IconType, Input, InputType } from "@ace/ace-ui-web";
import { SvgIconComponent } from "@mui/icons-material";
import styles from "./formfield.module.css";

interface FormFieldProps {
    label: string;
    value: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
    type?: InputType;
    placeholder?: string;
    icon?: IconType;
    muiIcon?: SvgIconComponent;
    iconSize?: { height: number; width: number };
    error?: string;
    helperText?: string;
    disabled?: boolean;
    readOnly?: boolean;
    hideLabel?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
    label,
    value,
    onChange,
    onKeyDown,
    type = InputType.Text,
    placeholder,
    icon,
    muiIcon: MuiIcon,
    iconSize = { height: 15, width: 12 },
    error,
    helperText,
    disabled = false,
    readOnly = false,
    hideLabel = false,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onKeyDown || !containerRef.current) return;
        const input = containerRef.current.querySelector("input");
        if (!input) return;
        input.addEventListener("keydown", onKeyDown);
        return () => {
            input.removeEventListener("keydown", onKeyDown);
        };
    }, [onKeyDown]);

    return (
        <div ref={containerRef} className={[styles.formGroup, "form-group"].join(" ")}>
            {!hideLabel && (
                <div className={`${styles.formGrid} ${styles.formGridIconCols}`}>
                    {icon && (
                        <Icon
                            iconType={icon}
                            style={{ height: iconSize.height, width: iconSize.width }}
                            color="gray"
                        />
                    )}
                    {MuiIcon && (
                        <MuiIcon sx={{ height: 1, fontSize: 15, color: "gray" }} />
                    )}
                    <p className={styles.formLabel}>{label}</p>
                </div>
            )}
            <Input
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled || readOnly}
            />
            {helperText && !error && (
                <p className={styles.helperText}>{helperText}</p>
            )}
            {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
    );
};

export default FormField;
